import { ChangeEvent, KeyboardEvent, ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	Button,
	PageControlBarModule,
	TabMenu,
	EmissionsSummaryPanelModule,
	SearchableSelect,
	SortablePaginatedTableModule, InlineAlert } from '../../components/BaseComponents'
import CanonicalModal from '../../components/BaseComponents/CanonicalModal/CanonicalModal'
import { useAppDispatch, useAppSelector } from '../../redux/hooks/reduxHooks'
import {
	selectCarbonSummary,
	selectCarbonSummaryError,
	selectCarbonSummaryStatus,
	selectTotalEmissionsSummary,
} from '../../redux/slices/carbonAccountSummarySlice'
import { getCarbonSummaryData } from '../../redux/actions/carbonAccountSummaryAction'
import { resendInvitationCompany } from '../../redux/actions/companySentInvitationsActions'
import { FetchStatus, PostStatus } from '../../types/LoadingStates'
import routes from '../../components/Router/routes'
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch'
import useCurrencyFormatter from '../../hooks/useCurrencyFormatter'
import useCurrency from '../../hooks/useCurrency'
import { getCurrencySymbol } from '../../lib/utils'
import useApi from '../../hooks/useApi'
import { useCarbonAccountsPeriod } from './useCarbonAccountsPeriod'
import { carbonAccountsGranularities, CarbonAccountsGranularityId } from './carbonAccountsGranularities'
import { errorText } from './carbonAccountsErrorText'
import { labelOnlySubstringFilter } from '../../utils/dropdownFilters'

export type CarbonAccountsZones = {
	primarySelector: { label: string; controlId: string; children: ReactNode }
	summary: ReactNode
	tabs: ReactNode
	viewSelector: ReactNode
	filtersContent: ReactNode
	actions: ReactNode
	table: ReactNode
}

type UseCarbonAccountsZonesArgs = {
	granularity: CarbonAccountsGranularityId
	setGranularity: (next: CarbonAccountsGranularityId) => void
}

export function useCarbonAccountsZones({ granularity, setGranularity }: UseCarbonAccountsZonesArgs): CarbonAccountsZones {
	const formatCurrency = useCurrencyFormatter()
	const currency = useCurrency()
	const navigate = useNavigate()
	const dispatch = useAppDispatch()
	const api = useApi()
	const periodSelectorId = useId()

	const entry = carbonAccountsGranularities[granularity]

	const {
		accountingPeriodsStatus,
		accountingPeriodsError,
		accountingTypeOptions,
		thisAccountingPeriod,
		companyId,
		accountingPeriodIdVal,
		setAccountingPeriod,
		updateStatus,
	} = useCarbonAccountsPeriod()

	const [scope, setScope] = useState('')
	const [orderBy, setOrderBy] = useState<string>(entry.defaultSort.key)
	const [direction, setDirection] = useState<'asc' | 'desc' | undefined>('desc')
	const [searchKeyword, setSearchKeyword] = useState('')
	const [resendCandidate, setResendCandidate] = useState<{ invitationId: string; supplierName: string } | null>(null)

	const dataStatus = useAppSelector(entry.selectStatus)
	const dataError = useAppSelector(entry.selectError)
	const rowsData = useAppSelector(entry.selectData)
	const pagination = useAppSelector(entry.selectPagination)

	const carbonSummaryStatus = useAppSelector(selectCarbonSummaryStatus)
	const carbonSummaryError = useAppSelector(selectCarbonSummaryError)
	const carbonSummary = useAppSelector(selectCarbonSummary)
	const totalCarbonSummary = useAppSelector(selectTotalEmissionsSummary)

	const isLoading =
		accountingPeriodsStatus === FetchStatus.loading ||
		carbonSummaryStatus === FetchStatus.loading ||
		dataStatus === FetchStatus.loading ||
		updateStatus === PostStatus.sending

	const fetchData = (
		page: number,
		sortKey?: string,
		sortDirection?: string,
		scopeFilter?: string,
		query?: string
	) => {
		if (companyId === '' || accountingPeriodIdVal === '') {
			return
		}
		dispatch(
			entry.fetch(api, {
				companyId,
				accountingPeriodId: accountingPeriodIdVal,
				page,
				limit: entry.limit,
				orderBy: sortKey ?? orderBy,
				direction: sortDirection ?? direction,
				scope: scopeFilter ?? scope,
				q: query ?? searchKeyword,
			})
		)
	}

	const runSearch = () => {
		fetchData(pagination.first_page, undefined, undefined, undefined, searchKeyword)
	}
	const { searchInProgress, flush: flushSearch } = useDebouncedSearch(searchKeyword, runSearch)

	const handleScopeChange = (newScope: string) => {
		setScope(newScope)
		fetchData(pagination.first_page, undefined, undefined, newScope, searchKeyword)
	}

	const handlePeriodChange = (option: any) => {
		setAccountingPeriod(option)
		fetchData(pagination.first_page)
		dispatch(getCarbonSummaryData(api, option.value))
	}

	// Initial fetch once the reporting period/company are known (mirrors mount behaviour).
	useEffect(() => {
		if (companyId !== '' && accountingPeriodIdVal !== '') {
			fetchData(pagination.first_page)
			dispatch(getCarbonSummaryData(api, accountingPeriodIdVal))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyId, accountingPeriodIdVal])

	const onResendInvite = (invitationId: string, supplierName: string) => {
		setResendCandidate({ invitationId, supplierName })
	}
	const columns = useMemo(
		() => entry.buildColumns({ navigate, formatCurrency, onResendInvite }),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[entry, navigate, formatCurrency]
	)
	const tableData = entry.toRows(rowsData)

	// Toggling granularity keeps scope/search/sort state (it lives here, not in a remounted
	// child), except search always clears (it targets a different entity per granularity) and
	// sort falls back to the new granularity's default when the current key doesn't apply there.
	const previousGranularityRef = useRef(granularity)
	useEffect(() => {
		const previous = previousGranularityRef.current
		if (previous === granularity) {
			return
		}
		previousGranularityRef.current = granularity

		// The outgoing granularity's data is stale by the time it's revisited - clear it rather
		// than showing a flash of old results (this replaces the old, silently-broken RESET dispatch).
		dispatch(carbonAccountsGranularities[previous].reset())

		const nextColumnIds = new Set(columns.map((column) => column.id))
		const nextOrderBy = nextColumnIds.has(orderBy) ? orderBy : entry.defaultSort.key
		const nextDirection = nextColumnIds.has(orderBy)
			? direction
			: entry.defaultSort.direction === 'ascending'
				? 'asc'
				: 'desc'

		setSearchKeyword('')
		setOrderBy(nextOrderBy)
		setDirection(nextDirection)

		if (companyId !== '' && accountingPeriodIdVal !== '') {
			fetchData(pagination.first_page, nextOrderBy, nextDirection, scope, '')
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [granularity])

	const confirmResendInvite = () => {
		if (!resendCandidate) return
		dispatch(resendInvitationCompany(api, resendCandidate.invitationId)).then(() => fetchData(pagination.current_page))
		setResendCandidate(null)
	}

	return {
		primarySelector: {
			label: 'Select Reporting Period',
			controlId: periodSelectorId,
			children: (
				<SearchableSelect
					inputId={periodSelectorId}
					options={accountingTypeOptions}
					placeholder='DD/MM/YY - DD/MM/YY'
					isClearable={false}
					onChange={handlePeriodChange}
					value={accountingTypeOptions.filter((option) => option.value === thisAccountingPeriod?.id)}
					filterOption={labelOnlySubstringFilter}
				/>
			),
		},
		summary: (
			<EmissionsSummaryPanelModule
				carbonSummary={carbonSummary}
				totalEmissions={totalCarbonSummary}
				intensityRatioUnit={`${getCurrencySymbol(currency)} of turnover`}
				activeScope={scope as '' | '1' | '2' | '3'}
				loading={carbonSummaryStatus === FetchStatus.loading}
				error={errorText(carbonSummaryError)}
			/>
		),
		tabs: (
			<TabMenu
				tabs={[
					{ id: '', label: 'Overview' },
					{ id: '1', label: 'Scope 1' },
					{ id: '2', label: 'Scope 2' },
					{ id: '3', label: 'Scope 3 (upstream)' },
				]}
				value={scope}
				onChange={handleScopeChange}
				ariaLabel='Emissions scope'
				testId='scope-tabs'
				additionalClass='hidden sm:block'
			/>
		),
		viewSelector: (
			<PageControlBarModule.RadioGroup
				label='View'
				name='carbon-accounts-view'
				options={(Object.keys(carbonAccountsGranularities) as CarbonAccountsGranularityId[]).map((id) => ({
					id,
					label: carbonAccountsGranularities[id].option.label,
					checked: granularity === id,
					onChange: () => setGranularity(id),
				}))}
			/>
		),
		filtersContent: (
			<PageControlBarModule.Primary>
				<PageControlBarModule.Search
					name={entry.search.name}
					label={entry.search.label}
					placeholder={entry.search.placeholder}
					value={searchKeyword}
					onChange={(event: ChangeEvent<HTMLInputElement>) => setSearchKeyword(event.target.value)}
					onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
						if (event.key === 'Enter') {
							flushSearch()
						}
					}}
					onClear={() => {
						setSearchKeyword('')
						fetchData(pagination.first_page, undefined, undefined, undefined, '')
					}}
					isLoading={searchInProgress}
				/>
			</PageControlBarModule.Primary>
		),
		actions: (
			<Button
				requiresWrite
				type='Primary'
				size='sm'
				text='Publish accounts'
				additionalClass='h-[42px]'
				onClick={() => {
					navigate(routes.myReportsCreate.path, { state: { periodId: thisAccountingPeriod?.id } })
				}}
			/>
		),
		table: (
			<>
				<SortablePaginatedTableModule
					isLoading={isLoading}
					data={tableData}
					columns={columns}
					sort={entry.defaultSort}
					onSortChange={(key, sortDirection) => {
						setOrderBy(key)
						setDirection(sortDirection === 'ascending' ? 'asc' : 'desc')
						fetchData(pagination.current_page, key, sortDirection, scope, searchKeyword)
					}}
					pagination={pagination}
					onPageChange={(newPageNumber) => fetchData(newPageNumber)}
					error={
						dataStatus === FetchStatus.error ? (
							<>
								<p className='text-white'>
									There was an error fetching the {entry.option.label.toLowerCase()} data for this
									accounting period ({thisAccountingPeriod?.id}). If this problem persists, please
									contact the Neoni team.
								</p>
								<p className='text-white'>{errorText(dataError)}</p>
							</>
						) : undefined
					}
				/>

				{accountingPeriodsStatus === FetchStatus.error && (
					<InlineAlert type='error' className='mt-4'>						<p className='text-white'>
							There was an error fetching the accounting periods list. If this problem persists,
							please contact the Neoni team.
						</p>
						<p className='text-white'>{errorText(accountingPeriodsError)}</p>
					</InlineAlert>
				)}
				{!thisAccountingPeriod && accountingPeriodsStatus === FetchStatus.loaded && (
					<InlineAlert type='error' className='mt-4'>						<p className='text-white'>
							There are no completed accounting periods to view. If this problem persists, please
							contact the Neoni team.
						</p>
					</InlineAlert>
				)}

				{granularity === 'suppliers' && (
					<CanonicalModal
						open={!!resendCandidate}
						onClose={() => setResendCandidate(null)}
						title='Resend invite'
						actions={
							<>
								<Button requiresWrite type='Primary' size='sm' text='Resend invite' onClick={confirmResendInvite} additionalClass='mr-5' />
								<Button type='Outlined' size='sm' text='Cancel' onClick={() => setResendCandidate(null)} />
							</>
						}
					>
						<p className='text-white mt-8'>
							Are you sure you want to resend the invitation to {resendCandidate?.supplierName}?
						</p>
					</CanonicalModal>
				)}
			</>
		),
	}
}
